import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import Toggle from '../../../baseComponents/gui/toggle/Toggle';
import Scrollbar from 'react-scrollbars-custom';
import { customizationScrollbarSettings } from '../../../../constants/copyright';
import CustomModal from '../../../baseComponents/gui/customModal/CustomModal';
import { useModal } from '../../../../hooks/useModal';
import CustomButton from '../../../features/customButton/ui/main/CustomButton';
import { safeHTML } from '../../../../utils/safeHTML';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useUser } from '../../../../redux/reducer/user';
import numWord from '../../../../utils/numWord';
import { useAccountMode } from '../../../../hooks/useAccountMode';
import { useContent } from '../../../../redux/reducer/content';
import { useRequestsHandlerData } from '../../../../hooks/useRequestsHandlerData';
import AppLoader from '../../../features/appLoader/AppLoader';
import { aboutSubscription } from '../../../../constants/copyright-modals';
import { secondsToDays } from '../../../../utils/helpers/helpers';
import { useTranslation } from '../../../../translations';
import useGetFullAchievementsList from '../../../../hooks/shared/achievements/useGetFullAchievementsList';
import { useApplicationCallbacks } from '../../../../providers/application-callbacks';

export const PassModal = React.forwardRef(function PassModal(
  { img, title, end, bonuses, subtitle, items, link, note, note2, sign, buttons, buyButtons },
  ref,
) {
  const {
    profile: { premiumElapsedTime, gacha_event_cash, iconId },
  } = useUser();
  const { premiumAccounts } = useContent();
  const { id, addModal, closeModal } = useModal();
  const { mode: accountMode } = useAccountMode();
  const [customState, setCustomState] = useState(0);
  const getFullAchievements = useGetFullAchievementsList();
  const necessaryAchievements = getFullAchievements()?.filter?.(({ needed_premium }) => needed_premium);
  const { t } = useTranslation();
  const { remainder: premiumRemainder, wordArray } = secondsToDays(premiumElapsedTime, t);
  const callbacks = useApplicationCallbacks();

  const accountsData = useMemo(
    () =>
      (premiumAccounts || []).map(({ id, cost, premium_time }) => {
        const { remainder, wordArray } = secondsToDays(premium_time, t);
        return { id, cost, premium_time, action: id, text: `${remainder} ${numWord(remainder, wordArray)}` };
      }),
    [premiumAccounts],
  );

  const shopState = useMemo(
    () =>
      premiumAccounts.reduce(
        (acc, { id }, index) => ({
          ...acc,
          [id]: () => setCustomState(index),
        }),
        {},
      ),
    [premiumAccounts],
  );

  const currentPremium = useMemo(() => accountsData[customState], [accountsData, customState]);

  const activeButtons = buttons.filter(
    ({ action }) =>
      ({
        standard: true,
        premium: { buy: false, close: true }[action],
      }[accountMode]),
  );

  const buttonCallbacks = {
    buy: () => {
      if (gacha_event_cash < currentPremium.cost) return;

      const { remainder: buyPremiumRemainder, wordArray: buyPremiumWordArray } = secondsToDays(
        currentPremium.premium_time,
        t,
      );
      addModal({
        type: 'buyModal',
        props: {
          itemData: { priceData: { giftCost: currentPremium?.cost } },
          title: `${t('buyModal.premium.title', {
            days: `${buyPremiumRemainder} ${numWord(buyPremiumRemainder, buyPremiumWordArray)}`,
          })}`,
          callbacks: { buy: () => callbacks.buyPremium(currentPremium.id) },
          buttons: buyButtons,
        },
      });
    },
    close: () => {
      closeModal({ id });
    },
    terms: () => {
      addModal({ type: 'slideModal', props: { ...aboutSubscription, mod: 'subscription' } });
    },
  };

  const equipIcon = (id) => {
    ({
      premium: () => {
        const iconData = { ...(id !== iconId && { id }) };
        callbacks.setPremiumIcon(iconData);
      },
    })[accountMode]?.();
  };

  useEffect(() => {
    callbacks.getIcons();
  }, []);

  const isPending = useRequestsHandlerData([{ address: 'requestsPremiumBuy' }, { address: 'requestsSetPremiumIcon' }]);

  return (
    <CustomModal className={'custom-modal_fullpage'} animation={['fade', 'scale', 'scale-bounce']}>
      <div className={'pass-modal'} ref={ref}>
        <div className={'pass-modal__content'}>
          <Scrollbar {...customizationScrollbarSettings}>
            <img src={img} className={'pass-modal__image'} alt={''} />
            <h2 className={'pass-modal__title'}>{t(title)}</h2>

            {!!premiumRemainder && (
              <div className={'pass-modal__end'}>
                <div className={'pass-modal__end-note'}>{t('other.endIn')}</div>
                <div className={'pass-modal__end-time'}>
                  {premiumRemainder} {numWord(premiumRemainder, wordArray)}
                </div>
              </div>
            )}

            <p className={'pass-modal__subtitle'}>{safeHTML(t(subtitle))}</p>

            <div className={'pass-modal__toggle'}>
              <Toggle list={accountsData} funcs={shopState} startActive={customState} className={'toggle_pass'} />
            </div>

            <div className={'pass-modal__items'}>
              {bonuses?.[customState]?.map?.(({ img, text }, id) => (
                <div key={`p-item-${id}`} className={'pass-modal__item'}>
                  <img src={img} className={'pass-modal__item-image'} alt={''} />
                  <div className={'pass-modal__item-text'}>{safeHTML(t(text))}</div>
                </div>
              ))}
            </div>

            <p className={'pass-modal__note'}>{safeHTML(t(premiumRemainder ? note2 : note))}</p>
            <p className={'pass-modal__sign'}>{safeHTML(t(sign))}</p>

            <div className={'pass-modal__list'}>
              <Swiper watchOverflow={true} slidesPerView={'auto'}>
                {necessaryAchievements?.map(({ icon, id }) => {
                  const isShowButton = { premium: true, standard: false }[accountMode];
                  const isEquipped = id === iconId;

                  return (
                    <SwiperSlide key={`p-list-item-${id}`}>
                      <div className={'pass-modal__list-item'}>
                        <img src={icon} className={'pass-modal__list-item-image'} alt={''} />
                        {isShowButton && (
                          <CustomButton
                            className={classNames('pass-modal__list-item-button button_my-gift', {
                              [`button_my-gift-equip`]: id === isEquipped,
                            })}
                            timeout={3000}
                            text={!isEquipped && t('other.select')}
                            icon={isEquipped && 'equipped'}
                            onClick={() => equipIcon(id)}
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>

            <CustomButton {...link} onClick={buttonCallbacks['terms']} />

            <div className={'pass-modal__buttons'}>
              {activeButtons.map((item, id) => {
                const { action, text, count, ...otherProps } = item;
                return (
                  <CustomButton
                    key={`b-${id}`}
                    {...otherProps}
                    onClick={buttonCallbacks[action]}
                    text={text}
                    {...(count && { count: currentPremium.cost })}
                    disabled={{ buy: gacha_event_cash < currentPremium.cost }[action]}
                  />
                );
              })}
            </div>
          </Scrollbar>
        </div>
        {isPending && <AppLoader />}
      </div>
    </CustomModal>
  );
});
